import {
  labelize,
  readiness,
  stageReason,
  text,
} from './PlaceWorkbenchParts';

const APPROVAL_TRANSITION_ISSUES = new Set([
  'last_human_reviewed_at',
  'card_not_approved',
  'card_judgment_needed',
]);

const ACTIVE_PROPOSAL_STATUSES = new Set(['pending', 'needs_followup', 'proposed']);

const VERSION_FIELD_ORDER = [
  'card_title',
  'pitch',
  'caveat',
  'short_guest_copy',
  'card_note_for_agent',
  'recommendation_angle',
  'retrieval_lane',
  'category',
  'utility_kind',
  'practical_note',
  'tags',
  'best_for',
  'booking_logic',
  'seasonality',
  'weather_logic',
  'budget_signal',
  'parking_access',
  'confidence',
  'freshness_risk',
  'place_name',
  'area',
  'town_or_community',
  'website_url',
  'source_url',
  'phone',
  'email',
];

export function normalizedCardTitle(value) {
  return text(value).replace(/\s+/g, ' ').toLowerCase();
}

export function approvalReadiness(card) {
  const state = readiness(card);
  const missing = state.missing.filter(issue => !APPROVAL_TRANSITION_ISSUES.has(issue));
  const blockers = state.blockers.filter(issue => !APPROVAL_TRANSITION_ISSUES.has(issue));
  return {
    ...state,
    missing,
    blockers,
    issueCount: missing.length + blockers.length,
  };
}

export function cardLifecycleLabel(card) {
  if (card?.is_unsaved) return 'Unsaved';
  if (card?.is_archived || card?.archived_at) return 'Archived';
  if (card?.publish_status === 'published') return 'Published';
  if (card?.publish_status === 'approved') return 'Approved';
  return approvalReadiness(card).issueCount === 0 ? 'Draft · ready to approve' : 'Draft · blocked';
}

export function proposalPayload(item) {
  return item?.payload || item || {};
}

export function proposalKind(item) {
  return item?.item_type || proposalPayload(item).item_type || proposalPayload(item).request_kind || 'proposal';
}

export function isActiveProposalItem(item) {
  const status = item?.status || proposalPayload(item).status || proposalPayload(item).overall_status || proposalPayload(item).field_status;
  return ACTIVE_PROPOSAL_STATUSES.has(status);
}

function enrichmentRunCounts(run) {
  const summary = run?.summary || {};
  return [
    ['Fields', summary.field_proposals],
    ['Signals', summary.match_signal_assignments],
    ['Tags', summary.proposed_tags],
    ['Hours', summary.hours_assertions],
    ['Warnings', summary.warnings],
    ['Flags', run?.flags ?? summary.flags],
  ]
    .map(([label, value]) => [label, Number(value || 0)])
    .filter(([, value]) => value > 0);
}

export function latestEnrichmentRunMessage(run) {
  if (!run) return null;
  if (run.status === 'failed') return run.error || 'Failed';
  if (['queued', 'pending'].includes(run.status)) return 'Queued';
  if (run.status === 'running') return 'Running';
  if (run.status === 'completed') {
    const counts = enrichmentRunCounts(run);
    if (counts.length === 0) return 'Completed with no new proposals';
    return counts.map(([label, value]) => `${label} ${value}`).join(' · ');
  }
  return labelize(run.status);
}

export function latestEnrichmentRunKind(run) {
  if (!run) return 'neutral';
  if (run.status === 'failed') return 'warning';
  if (['queued', 'pending', 'running'].includes(run.status)) return 'accent';
  return run.has_actionable_items ? 'success' : 'neutral';
}

export function proposalTier(item) {
  const tier = item?.tier || proposalPayload(item).tier;
  const kind = proposalKind(item);
  if (item?.blocking || tier === 'blocking') return 'blocking';
  if (tier === 'correction' || kind === 'field_update') return 'correction';
  return 'suggestion';
}

export function stageBannerClass(kind) {
  if (kind === 'danger') return 'border-red-200 bg-red-50 text-red-900';
  if (kind === 'warning') return 'border-yellow-200 bg-yellow-50 text-yellow-900';
  if (kind === 'accent') return 'border-blue-200 bg-blue-50 text-blue-900';
  if (kind === 'success') return 'border-green-200 bg-green-50 text-green-900';
  return 'border-stone-200 bg-stone-50 text-stone-900';
}

export function savedViewChipClass(viewId, active = false) {
  const classes = {
    needs_work: active ? 'border-yellow-600 bg-yellow-600 text-white' : 'border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100',
    ready_to_approve: active ? 'border-blue-600 bg-blue-600 text-white' : 'border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100',
    attention: active ? 'border-red-600 bg-red-600 text-white' : 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100',
    approved: active ? 'border-purple-600 bg-purple-600 text-white' : 'border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100',
    published: active ? 'border-green-600 bg-green-600 text-white' : 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100',
  };
  return classes[viewId] || (active ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-300 bg-white text-stone-600 hover:border-stone-500');
}

export function stageBannerSentence(card) {
  const stage = card?.stage;
  if (stage === 'needs_work') return `${card?.stage_reasons?.length || 0} issue${(card?.stage_reasons?.length || 0) === 1 ? '' : 's'} to fix.`;
  if (stage === 'ready_to_approve') return 'Content checks pass. Your review is the quality gate, and approval freezes an immutable version.';
  if (stage === 'approved') {
    return card?.live_version
      ? `Approved v${card.approved_version || '-'}; live v${card.live_version} is still serving.`
      : `Approved v${card?.approved_version || '-'}, not yet live.`;
  }
  if (stage === 'published') return card?.live_version ? `Live · v${card.live_version}` : 'Live.';
  if (stage === 'attention') return stageReason(card);
  if (stage === 'inactive') return 'Archived or retired; excluded from default review work.';
  return stageReason(card);
}

export function versionSnapshotEntries(snapshot) {
  const order = new Map(VERSION_FIELD_ORDER.map((field, index) => [field, index]));
  return Object.entries(snapshot || {})
    .filter(([, value]) => value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0))
    .sort(([left], [right]) => {
      const leftIndex = order.has(left) ? order.get(left) : VERSION_FIELD_ORDER.length;
      const rightIndex = order.has(right) ? order.get(right) : VERSION_FIELD_ORDER.length;
      return leftIndex === rightIndex ? left.localeCompare(right) : leftIndex - rightIndex;
    });
}
